import * as React from "react"
import { createId } from "@paralleldrive/cuid2"
import { FileIcon, Paperclip, X } from "lucide-react"

import { AddAttachmentDialog } from "@/components/shared/attachments"
import { Button } from "@/components/shadcn/ui/button"
import { buildFormFieldDomId } from "@/components/shared/attachments/navigation"
import { cn } from "@/lib/utils"
import type { PendingFieldMediaBundle } from "./report-creation-media"

interface Props {
  locator: string
  hasFieldMedia: boolean
  canCreateFieldMedia: boolean
  isSelected: boolean
  onNavigateToFieldMedia?: (locator: string, options?: { openComposer?: boolean }) => void
  pendingFieldMedia?: PendingFieldMediaBundle
  onPendingFieldMediaChange?: (bundle: PendingFieldMediaBundle | undefined) => void
  children: React.ReactNode
}

const FieldMediaNavigationWrapper = ({
  locator,
  hasFieldMedia,
  canCreateFieldMedia,
  isSelected,
  onNavigateToFieldMedia,
  pendingFieldMedia,
  onPendingFieldMediaChange,
  children,
}: Props) => {
  const [isComposerOpen, setIsComposerOpen] = React.useState(false)
  const handlePendingFieldMediaChange = onPendingFieldMediaChange
  const isCreating = Boolean(handlePendingFieldMediaChange)
  const hasPendingMedia = Boolean(pendingFieldMedia?.uploads.length)
  const hasMedia = hasFieldMedia || hasPendingMedia
  const actionLabel = hasFieldMedia
    ? canCreateFieldMedia
      ? "Manage evidence"
      : "View evidence"
    : hasPendingMedia
      ? "Manage evidence"
      : "Attach evidence"

  React.useEffect(() => {
    if (!isSelected) {
      return
    }

    const fieldElement = document.getElementById(buildFormFieldDomId(locator))
    fieldElement?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [isSelected, locator])

  return (
    <div
      id={buildFormFieldDomId(locator)}
      className={cn(
        "rounded-lg transition-colors",
        isSelected && "bg-sky-50/80 ring-1 ring-sky-200",
      )}
    >
      {children}
      {canCreateFieldMedia && isCreating ? (
        <div className="-mt-2 px-4 pb-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 rounded-full border border-slate-200 bg-slate-50/80 px-2.5 text-[11px] font-medium text-slate-600 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900"
            onClick={() => setIsComposerOpen(true)}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {hasPendingMedia ? "Add evidence" : "Attach evidence"}
          </Button>
          {hasPendingMedia ? (
            <ul className="mt-2 space-y-2">
              {pendingFieldMedia?.uploads.map((upload) => (
                <li
                  key={upload.uploadId}
                  className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
                >
                  <FileIcon className="h-4 w-4 shrink-0 text-slate-500" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                    {upload.file.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${upload.file.name}`}
                    onClick={() => {
                      const uploads = pendingFieldMedia.uploads.filter(
                        ({ uploadId }) => uploadId !== upload.uploadId,
                      )
                      handlePendingFieldMediaChange!(
                        uploads.length > 0 ? { ...pendingFieldMedia, uploads } : undefined,
                      )
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <AddAttachmentDialog
            open={isComposerOpen}
            onOpenChange={setIsComposerOpen}
            fieldLabel={pendingFieldMedia?.target.fieldLabel}
            mode="fieldMedia"
            onSave={({ file, note }) => {
              if (!pendingFieldMedia) return
              handlePendingFieldMediaChange!({
                ...pendingFieldMedia,
                ...(note ? { note } : {}),
                uploads: [...pendingFieldMedia.uploads, { uploadId: createId(), file }],
              })
              setIsComposerOpen(false)
            }}
          />
        </div>
      ) : null}
      {(hasMedia || canCreateFieldMedia) && onNavigateToFieldMedia && !isCreating && (
        <div className="-mt-2 px-4 pb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 rounded-full border border-slate-200 bg-slate-50/80 px-2.5 text-[11px] font-medium text-slate-600 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900",
              isSelected &&
                "border-sky-300 bg-sky-100 text-sky-900 hover:border-sky-400 hover:bg-sky-100",
            )}
            onClick={() => onNavigateToFieldMedia(locator, { openComposer: !hasMedia })}
          >
            <Paperclip className="h-3.5 w-3.5" />
            <span>{actionLabel}</span>
            <span
              aria-hidden="true"
              className={cn("text-slate-400", isSelected && "text-sky-700/70")}
            >
              •
            </span>
            <span className={cn("text-slate-500", isSelected && "text-sky-800/80")}>
              Attachments
            </span>
          </Button>
        </div>
      )}
    </div>
  )
}

export default FieldMediaNavigationWrapper
